import { useEffect, useRef, useState } from 'react';

export type WsState = 'idle' | 'connecting' | 'open' | 'closed';

export type WsHandle = {
  state: WsState;
  send: (msg: unknown) => void;
};

export function useWebSocket<TIncoming = unknown>(
  url: string | null,
  onMessage: (msg: TIncoming) => void,
  options?: { onOpen?: (send: (msg: unknown) => void) => void },
): WsHandle {
  const [state, setState] = useState<WsState>('idle');
  const wsRef = useRef<WebSocket | null>(null);
  const onMessageRef = useRef(onMessage);
  const onOpenRef = useRef(options?.onOpen);
  onMessageRef.current = onMessage;
  onOpenRef.current = options?.onOpen;

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    let retry = 0;

    const connect = () => {
      if (cancelled) return;
      setState('connecting');
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        retry = 0;
        setState('open');
        onOpenRef.current?.((msg) => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
        });
      };

      ws.onmessage = (ev) => {
        try {
          onMessageRef.current(JSON.parse(ev.data as string) as TIncoming);
        } catch {
          // ignore non-JSON
        }
      };

      ws.onclose = () => {
        setState('closed');
        if (cancelled) return;
        const delay = Math.min(2000 * 2 ** retry, 32000);
        retry += 1;
        setTimeout(connect, delay);
      };

      ws.onerror = () => {
        ws.close();
      };
    };

    connect();

    return () => {
      cancelled = true;
      wsRef.current?.close();
    };
  }, [url]);

  const send = (msg: unknown) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    }
  };

  return { state, send };
}
